import { useEffect, useState } from 'react'
import { Button, Modal } from 'antd'
import BulkActionConfirm from 'src/screens/venue-groups/BulkActionConfirm'
import { startAndPollVenueGroupRun } from 'src/screens/venue-groups/startAndPollVenueGroupRun'
import {
  downloadFailureReceipt,
  receiptProblemLines,
  resultLeftIngredientsOff,
  REVENUE_REFRESH_NOTE,
  revenueCategoryReceiptLines,
  shoppingListReceiptLines,
  restrictedSupplierReceiptLines,
  shoppingListLeftLinesOff,
  revenueRefreshOnly,
  runProgressText,
  runStartFailureMessage,
} from 'src/screens/venue-groups/runReceipt'

/**
 * Confirm, then poll, then the receipt. Used by every bulk action, including dummy.
 */
const VenueGroupRunDialog = ({
  open,
  groupId,
  action,
  payload,
  onClose,
  onFinished,
  sourceTitle,
}) => {
  const [phase, setPhase] = useState('confirm')
  const [run, setRun] = useState(null)
  const [startError, setStartError] = useState('')

  useEffect(() => {
    if (!open) {
      setPhase('confirm')
      setRun(null)
      setStartError('')
    }
  }, [open])

  /** Esc / Back / Close: drop run state so a later Review is not stale. */
  const dismiss = () => {
    setPhase('confirm')
    setRun(null)
    setStartError('')
    onClose?.()
  }

  const start = async flags => {
    setPhase('running')
    const nextPayload = {
      ...(payload || {}),
      ...(flags?.replaceAllLocalChanges
        ? { replaceAllLocalChanges: true }
        : {}),
      ...(flags?.replaceVenueIds?.length
        ? { replaceVenueIds: flags.replaceVenueIds }
        : {}),
      ...(flags?.onlyMissing ? { onlyMissing: true } : {}),
      ...(flags?.forceShare ? { forceShare: true } : {}),
    }
    try {
      const finished = await startAndPollVenueGroupRun({
        groupId,
        action,
        payload: nextPayload,
        onProgress: setRun,
      })
      setRun(finished)
      setPhase('receipt')
      if (finished?.status === 'finished') {
        onFinished?.({
          action,
          updated: finished?.counts?.updated || 0,
          skipped: finished?.counts?.skipped || 0,
          failed: finished?.counts?.failed || 0,
          at:
            finished?.updatedAt ||
            finished?.createdAt ||
            new Date().toISOString(),
          results: finished?.results || [],
        })
      }
    } catch (error) {
      setStartError(runStartFailureMessage(error))
      setPhase('receipt')
    }
  }

  const counts = run?.counts || {}
  const problems = (run?.results || []).filter(
    result =>
      result.outcome === 'failed' ||
      result.outcome === 'skipped' ||
      resultLeftIngredientsOff(result) ||
      (action === 'apply_shopping_list' && shoppingListLeftLinesOff(result)),
  )

  if (!open) return null

  return (
    <>
      {phase === 'confirm' && (
        <BulkActionConfirm
          open
          groupId={groupId}
          action={action}
          payload={payload}
          sourceTitle={sourceTitle}
          onCancel={dismiss}
          onConfirm={start}
        />
      )}
      {phase !== 'confirm' && (
        <Modal
          visible
          destroyOnClose
          keyboard={phase === 'receipt'}
          maskClosable={phase === 'receipt'}
          title={phase === 'receipt' ? 'Receipt' : 'Progress'}
          footer={
            phase === 'receipt'
              ? [
                  <Button
                    key='failures'
                    disabled={problems.length === 0}
                    onClick={() => downloadFailureReceipt(run?.results, action)}
                  >
                    Download skipped and failed
                  </Button>,
                  <Button key='close' type='primary' onClick={dismiss}>
                    Close
                  </Button>,
                ]
              : null
          }
          closable={phase === 'receipt'}
          onCancel={dismiss}
        >
          <p>
            {startError ||
              (revenueRefreshOnly(run?.results)
                ? REVENUE_REFRESH_NOTE
                : runProgressText(run))}
          </p>
          {phase === 'receipt' &&
            !startError &&
            action === 'assign_revenue_categories' &&
            revenueCategoryReceiptLines(run?.results).map(line => (
              <p key={line}>{line}</p>
            ))}
          {phase === 'receipt' &&
            !startError &&
            action === 'apply_shopping_list' &&
            shoppingListReceiptLines(run?.results).map(line => (
              <p key={line}>{line}</p>
            ))}
          {phase === 'receipt' &&
            !startError &&
            action === 'copy_restricted_suppliers' &&
            restrictedSupplierReceiptLines(run?.results).map(line => (
              <p key={line}>{line}</p>
            ))}
          {phase === 'receipt' && !startError && (
            <p>
              {counts.updated || 0} updated. {counts.skipped || 0} skipped.{' '}
              {counts.failed || 0} failed.
            </p>
          )}
          {phase === 'receipt' &&
            action !== 'apply_shopping_list' &&
            action !== 'copy_restricted_suppliers' &&
            receiptProblemLines(run?.results).map(line => (
              <p key={line}>{line}</p>
            ))}
        </Modal>
      )}
    </>
  )
}

export default VenueGroupRunDialog
